import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { FileText } from 'lucide-react';

const formatCurrency = (value) => {
  if (value === null || value === undefined) return '$0';
  return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', minimumFractionDigits: 0 }).format(value);
};

// Normaliza ISO (YYYY-MM-DD) y legacy Access (M/D/YY) a clave ordenable YYYY-MM-DD
const fechaISO = (f) => {
  const s = String(f || '').trim();
  if (!s) return '';
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
  if (m) {
    const p1 = parseInt(m[1], 10);
    const p2 = parseInt(m[2], 10);
    let y = parseInt(m[3], 10);
    if (y < 100) y += 2000;
    const [mo, d] = p1 > 12 ? [p2, p1] : [p1, p2];
    return `${y}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  }
  return '';
};

const formatFecha = (f) => {
  const iso = fechaISO(f);
  return iso ? `${iso.slice(8, 10)}-${iso.slice(5, 7)}-${iso.slice(0, 4)}` : '-';
};

const isCancelada = (v) => v.cancelado === 1 || v.cancelado === true || v.cancelado === '1';

// Iter 295: sección fija al pie de Informe Costo OT / Informe por Proyecto
export default function FacturasEmitidasSection({ ots }) {
  const rows = [];
  (ots || []).forEach((ot) => {
    (ot.real?.ventasList || []).forEach((v) => rows.push({ otNum: ot.numero_ot, v }));
  });
  rows.sort((a, b) => fechaISO(b.v.fecha_emision || b.v.fecha).localeCompare(fechaISO(a.v.fecha_emision || a.v.fecha)));
  const vigentes = rows.filter((r) => !isCancelada(r.v));
  const totNeto = vigentes.reduce((s, r) => s + (r.v.neto || 0), 0);
  const totIva = vigentes.reduce((s, r) => s + (r.v.iva || 0), 0);
  const totTotal = vigentes.reduce((s, r) => s + (r.v.total || 0), 0);
  const anuladas = rows.length - vigentes.length;

  return (
    <Card data-testid="facturas-emitidas-section">
      <CardHeader className="py-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <FileText className="w-5 h-5 text-blue-600" />
          Facturas Emitidas ({vigentes.length} vigentes{anuladas > 0 ? ` · ${anuladas} anuladas` : ''})
        </CardTitle>
        <p className="text-xs text-muted-foreground">Todas las facturas de las OTs incluidas en el informe, ordenadas por fecha (más reciente primero)</p>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="text-xs bg-muted/50">
                <TableHead className="py-2">OT</TableHead>
                <TableHead className="py-2">N° Factura</TableHead>
                <TableHead className="py-2">Fecha</TableHead>
                <TableHead className="py-2 text-right">Neto</TableHead>
                <TableHead className="py-2 text-right">IVA</TableHead>
                <TableHead className="py-2 text-right">Total</TableHead>
                <TableHead className="py-2">Estado</TableHead>
                <TableHead className="py-2">Cond. Pago</TableHead>
                <TableHead className="py-2">OC Cliente</TableHead>
                <TableHead className="py-2">HES</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} className="text-center text-muted-foreground py-4">
                    Sin facturas emitidas para las OTs incluidas
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {rows.map(({ otNum, v }, i) => {
                    const cancel = isCancelada(v);
                    return (
                      <TableRow key={`fes-${v.id_venta || v.numero_factura}-${i}`} className={`text-xs ${cancel ? 'line-through opacity-60' : ''}`}>
                        <TableCell className="font-mono font-semibold">OT-{otNum}</TableCell>
                        <TableCell className="font-mono">{String(v.numero_factura || '').replace(/\.0$/, '')}</TableCell>
                        <TableCell>{formatFecha(v.fecha_emision || v.fecha)}</TableCell>
                        <TableCell className="text-right font-mono">{formatCurrency(v.neto)}</TableCell>
                        <TableCell className="text-right font-mono text-muted-foreground">{formatCurrency(v.iva)}</TableCell>
                        <TableCell className="text-right font-mono font-semibold">{formatCurrency(v.total)}</TableCell>
                        <TableCell>
                          {cancel ? (
                            <span className="text-red-600 font-semibold">Anulada</span>
                          ) : (
                            <span className="text-emerald-700">{v.estado || 'Emitida'}</span>
                          )}
                        </TableCell>
                        <TableCell className="text-[11px] text-muted-foreground">{v.condicion_pago || '-'}</TableCell>
                        <TableCell className="text-[11px] font-mono">{v.orden_compra || '-'}</TableCell>
                        <TableCell className="text-[11px] font-mono">{v.hes || '-'}</TableCell>
                      </TableRow>
                    );
                  })}
                  <TableRow className="bg-slate-800 text-white font-bold text-xs">
                    <TableCell colSpan={3} className="py-2">TOTAL (vigentes)</TableCell>
                    <TableCell className="py-2 text-right font-mono">{formatCurrency(totNeto)}</TableCell>
                    <TableCell className="py-2 text-right font-mono">{formatCurrency(totIva)}</TableCell>
                    <TableCell className="py-2 text-right font-mono">{formatCurrency(totTotal)}</TableCell>
                    <TableCell colSpan={4}></TableCell>
                  </TableRow>
                </>
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
